import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "axios";
import backgroundSvg from "../../assets/images/svg/not-connected-yet.svg";
import metaLoginUserSvg from "../../assets/images/svg/meta-login-user.svg";
import metaLoginPassSvg from "../../assets/images/svg/meta-login-pass.svg";
import { ROUTES } from "../../components/consts/routes.consts";
import { apiBaseUrl } from "../../components/consts/api-url.const";
import MetaAdsNavbar from "../../components/metaBuilder/MetaAdsNavbar";
import { isMetaAdsTokenExpired } from "../../utils/api.util";

const MetaAdsLogin: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // If metaAdsPublicToken exists and is not expired, skip login and go to meta-campaigns
  useEffect(() => {
    if (isMetaAdsTokenExpired()) return;
    const token = localStorage.getItem("metaAdsPublicToken");
    if (token) {
      navigate(ROUTES.META_ADS_SETUP_PUBLIC, { replace: true });
    }
  }, [navigate]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !password) {
      toast.error("Please enter email and password");
      return;
    }

    setIsLoading(true);

    try {
      const response = await axios.post(
        `${apiBaseUrl}/api/meta-builder/login`,
        {
          username: email,
          password,
        },
      );

      const { token, user, expiresAt } = response.data;

      // Store token, expiry, and user data
      localStorage.setItem("metaAdsPublicToken", token);
      if (expiresAt) localStorage.setItem("metaAdsTokenExpiresAt", expiresAt);
      localStorage.setItem("metaAdsUserEmail", user.email);
      localStorage.setItem("metaAdsUserId", user.userId);
      localStorage.setItem("metaAdsUserName", user.name);

      toast.success("Login successful!");

      // Redirect to meta-ads-builder public page
      navigate(ROUTES.META_ADS_SETUP_PUBLIC);
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.msg || "Login failed. Please try again.";
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {/* Custom Navbar */}
      <MetaAdsNavbar showDivider={false} />

      <div className="min-h-screen flex flex-col relative overflow-hidden">
        <MetaAdsNavbar showDivider={false} />

        {/* Background */}
        <img
          src={backgroundSvg}
          className="absolute inset-0 w-full h-full object-cover object-center"
          style={{ zIndex: 0 }}
        />

        {/* CENTER AREA */}
        <div className="relative z-10 flex-1 flex flex-col items-center justify-center">
          {/* Title */}
          <h1 className="text-white text-3xl mb-6">Meta Ads Builder</h1>

          {/* Divider */}
          <div
            className="px-[600px] border-b mb-8"
            style={{ borderColor: "#FFFFFF33" }}
          />

          {/* Login Card */}
          <div className="relative w-[730px] rounded-[40px]">
            {/* Gradient Border */}
            <div
              className="absolute inset-0 rounded-[40px]"
              style={{
                padding: "2px",
                background:
                  "linear-gradient(0deg, #FFFFFF 0%, #001AFF 44.71%, #1B76FF 100%)",
                WebkitMask:
                  "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
                WebkitMaskComposite: "xor",
                maskComposite: "exclude",
              }}
            />
            {/* Inner Glass */}
            <div className="relative px-16 py-12 text-white bg-[radial-gradient(ellipse_80%_40%_at_50%_0%,rgba(79,107,255,0.4)_0%,rgba(255,255,255,0.05)_100%)] rounded-[40px]">
              {/* Title */}
              <h1 className="text-white text-[24px] font-bold text-center mb-1">
                Login Page
              </h1>
              <p className="text-white text-[15px] text-center  mb-12">
                Please Enter Account Details
              </p>

              {/* Form */}
              <form onSubmit={handleLogin} className="space-y-10">
                {/* Username */}
                <div>
                  <div className="flex items-center gap-3 mb-2 text-white font-medium">
                    <img
                      src={metaLoginUserSvg}
                      alt="User"
                      className="w-5 h-5"
                    />
                    <span className="text-[16px] font-semibold mt-[1px]">
                      Username
                    </span>
                  </div>

                  <input
                    type="text"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter Your Username"
                    className="
    w-full
    bg-transparent
    py-3
    text-white
    placeholder:text-white/30
    outline-none

    border-b border-white/15

    focus:border-[#4F6BFF]
    focus:shadow-[0_1px_0_0_#4F6BFF]

    transition-all duration-300

    /* Fix autofill background */
    [&:-webkit-autofill]:bg-transparent
    [&:-webkit-autofill]:text-white
    [&:-webkit-autofill:hover]:bg-transparent
    [&:-webkit-autofill:hover]:text-white
    [&:-webkit-autofill:focus]:bg-transparent
    [&:-webkit-autofill:focus]:text-white
    [-webkit-text-fill-color:white]
    [transition:background-color_5000s_ease-in-out_0s]
  "
                    style={{
                      WebkitBoxShadow: "0 0 0 30px transparent inset",
                    }}
                  />
                </div>

                {/* Password */}
                <div>
                  <div className="flex items-center gap-3 mb-2 text-white font-medium">
                    <img
                      src={metaLoginPassSvg}
                      alt="Password"
                      className="w-5 h-5"
                    />

                    <span className="text-[16px] font-semibold mt-[1px]">
                      Password
                    </span>
                  </div>

                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter Your Password"
                    className="
    w-full
    bg-transparent
    py-3
    text-white
    placeholder:text-white/30
    outline-none

    border-b border-white/15

    focus:border-[#4F6BFF]
    focus:shadow-[0_1px_0_0_#4F6BFF]

    transition-all duration-300

    /* Fix autofill background */
    [&:-webkit-autofill]:bg-transparent
    [&:-webkit-autofill]:text-white
    [&:-webkit-autofill:hover]:bg-transparent
    [&:-webkit-autofill:hover]:text-white
    [&:-webkit-autofill:focus]:bg-transparent
    [&:-webkit-autofill:focus]:text-white
    [-webkit-text-fill-color:white]
    [transition:background-color_5000s_ease-in-out_0s]
  "
                    style={{
                      WebkitBoxShadow: "0 0 0 30px transparent inset",
                    }}
                  />
                </div>

                {/* Button */}
                <div className="flex justify-center mb-4">
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-[112px] h-[40px] rounded-full bg-white text-black font-semibold text-[16px]"
                  >
                    Log In
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="relative z-10 w-full mt-12 text-center mb-[70px]">
          <div className="flex flex-col items-center gap-3 text-white text-[18px]">
            <button
              onClick={() => navigate(ROUTES.META_PRIVACY_TERMS)}
              className="transition-colors duration-200 block w-full hover:text-gray-300 cursor-pointer"
            >
              Terms of Service
            </button>
            <button
              onClick={() =>
                navigate(`${ROUTES.META_PRIVACY_TERMS}#data-deletion`)
              }
              className="hover:text-white transition-colors duration-200 block w-full cursor-pointer"
            >
              Data Deletion Instructions (Meta/Facebook)
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default MetaAdsLogin;
